'use client';

import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import type { User, AuthTokens } from '@/core/types/auth.types';
import { api } from '@/core/lib/api-client';

/* -------------------------------------------------------------------------- */
/*  Types                                                                     */
/* -------------------------------------------------------------------------- */

interface LoginResponse {
  data: {
    user: User & { portalType: string };
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  };
}

interface MeResponse {
  data: User & { portalType: string };
}

interface RefreshResponse {
  data: {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  };
}

interface ImpersonateParams {
  targetRole: string;
  tenantId: string;
  propertyId?: string;
  targetName?: string;
}

interface AuthContextType {
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isImpersonating: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<User>;
  logout: () => void;
  register: (data: { firstName: string; lastName: string; email: string; password: string }) => Promise<void>;
  clearError: () => void;
  impersonateAs: (params: ImpersonateParams) => Promise<void>;
  /** Resolves true once back in the admin session; false means signed out. */
  returnToAdmin: () => Promise<boolean>;
}

/* -------------------------------------------------------------------------- */
/*  Storage helpers                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Readable session-hint cookie name, duplicated from
 * `server/lib/auth-cookies.ts`.
 *
 * Not imported: that module is server-side and pulls `next/server` in, which
 * does not belong in a client bundle. The same duplication exists for
 * ACCESS_COOKIE in middleware.ts, and `auth-cookie-contract.test.ts` fails if
 * the copies ever diverge — this one is covered there too.
 */
const SESSION_HINT_COOKIE = 'hms-has-session';

const STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  TENANT_ID: 'tenant_id',
  PROPERTY_ID: 'property_id',
  USER: 'auth_user',
  /** Stores the original admin session when impersonating */
  IMPERSONATION_BACKUP: 'impersonation_backup',
} as const;

/**
 * Persist the signed-in session.
 *
 * THE TOKENS ARE DELIBERATELY NOT STORED. The server sets them as HttpOnly
 * cookies on every sign-in path (login, 2FA, magic link, register,
 * register-hotel, same-origin OAuth), and the browser sends them
 * automatically. Writing them here as well would keep the exact exposure the
 * cookies exist to remove: `localStorage` is readable by ANY JavaScript on the
 * page, so one XSS — a compromised dependency, a bad third-party script — reads
 * the JWT directly.
 *
 * The parameters remain in the signature because callers pass the login
 * response through unchanged, and because the CROSS-ORIGIN OAuth bridge still
 * receives tokens it must store (a Set-Cookie scoped to the API origin cannot
 * reach the guest portal). That path writes them at its own call site, where
 * the exception is visible, rather than silently here.
 *
 * The non-secret user/tenant/property values stay: they drive the UI, are not
 * credentials, and reading them from storage avoids a round trip on boot.
 */
function persistAuth(user: User & { portalType?: string }, accessToken: string, refreshToken: string) {
  void accessToken;
  void refreshToken;
  // A new sign-in supersedes whatever the previous session left behind. A
  // legacy token or property id from the last user would otherwise outrank the
  // cookie in TenantProvider's resolution, and a platform admin signing in
  // after a hotel admin inherited that hotel's property.
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
  if (user.tenantId) localStorage.setItem(STORAGE_KEYS.TENANT_ID, user.tenantId);
  else localStorage.removeItem(STORAGE_KEYS.TENANT_ID);
  if (user.propertyId) localStorage.setItem(STORAGE_KEYS.PROPERTY_ID, user.propertyId);
  else localStorage.removeItem(STORAGE_KEYS.PROPERTY_ID);
}

function clearAuth() {
  Object.values(STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
}

function getStoredTokens(): AuthTokens | null {
  if (typeof window === 'undefined') return null;
  const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  if (accessToken && refreshToken) return { accessToken, refreshToken };
  return null;
}

function getStoredUser(): User | null {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(STORAGE_KEYS.USER);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

/* -------------------------------------------------------------------------- */
/*  Context                                                                   */
/* -------------------------------------------------------------------------- */

const AuthContext = createContext<AuthContextType | null>(null);

function hasImpersonationBackup(): boolean {
  if (typeof window === 'undefined') return false;
  return !!localStorage.getItem(STORAGE_KEYS.IMPERSONATION_BACKUP);
}

/**
 * Is the readable session-hint cookie present?
 *
 * The real auth cookies are HttpOnly and invisible here by design; this one is
 * set by the server in the same response purely so the client can tell "no
 * session at all" from "a session I cannot see". See SESSION_HINT_COOKIE in
 * server/lib/auth-cookies.ts.
 *
 * Matched on a cookie BOUNDARY, not with `includes()`: a bare substring test
 * would also match a cookie whose name merely ends with this one.
 */
function hasSessionHint(): boolean {
  if (typeof document === 'undefined') return false;
  return new RegExp(`(?:^|;\\s*)${SESSION_HINT_COOKIE}=1(?:;|$)`).test(document.cookie);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [tokens, setTokens] = useState<AuthTokens | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const hydratedRef = useRef(false);

  /* ---- Hydrate from localStorage on mount ---- */
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;

    const storedUser = getStoredUser();

    // Restore on the stored USER alone — the tokens are no longer here.
    //
    // They live in HttpOnly cookies now, which JavaScript cannot read by
    // design. Requiring `getStoredTokens()` would make every cookie session
    // appear signed out on reload: the user object is present, the cookie is
    // valid, and the app would still bounce them to login.
    //
    // The stored user is a UI hint, not the credential. `/auth/me` below is
    // what actually establishes the session, and it authenticates with the
    // cookie — so a stale or forged user object cannot grant access; it only
    // avoids a flash of logged-out chrome while that call is in flight.
    // Ask the server even when NOTHING is stored locally.
    //
    // This used to return early on a missing stored user, treating "no client
    // state" as "signed out". Under HttpOnly that is exactly backwards: the
    // cookie is the session and JavaScript cannot see it, so a valid session
    // in a fresh browser profile — or after any code that clears localStorage
    // — was declared signed out without ever asking. Measured in a real
    // browser: cookies present and accepted by the API, dashboard rendering
    // the sign-in page.
    //
    // The stored user remains a rendering hint that avoids a flash of
    // logged-out chrome; it is not what grants access. `/auth/me` authenticates
    // with the cookie, so when there is no hint we simply render the loading
    // state until it answers.
    if (storedUser) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setUser(storedUser);
    }

    // Check if this is an impersonated session
    setIsImpersonating(hasImpersonationBackup());

    // NOTHING TO ASK ABOUT — skip the probe entirely.
    //
    // An anonymous visitor on the login page has neither a readable session
    // hint nor a stored user. Asking anyway produced a 401 from /auth/me and
    // then a 400 from /auth/refresh in the console of the one page where being
    // signed out is the expected state, on every single visit.
    //
    // BOTH conditions are required, and the second is what makes this safe to
    // ship. Sessions created before the hint cookie existed have no hint but DO
    // have a stored user, so they still get verified exactly as before; only a
    // visitor with no evidence of any session at all is skipped. Treating a
    // missing stored user alone as "signed out" is the regression this file
    // already documents above — this does not reintroduce it, because the hint
    // is set by the server alongside the real cookie and survives localStorage
    // being cleared.
    if (!hasSessionHint() && !storedUser) {
      setIsLoading(false);
      return;
    }

    // Verify token is still valid by calling /me
    api.get<MeResponse>('/auth/me')
      .then((res) => {
        const freshUser = res.data;
        setUser(freshUser);
        // The server is the authority on "Login as": its HttpOnly session
        // swap leaves no local backup, so the local flag alone missed it and
        // screens offered saves the server then refused (403 IMPERSONATED).
        if (freshUser.impersonated) setIsImpersonating(true);
        // Tokens are no longer held in JS. The cookie IS the session; this
        // state exists only for the legacy pre-migration and cross-origin
        // OAuth paths that still receive token values.
        setTokens(getStoredTokens());
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(freshUser));
      })
      .catch(async () => {
        // /me failed — the access token is expired or absent. Ask for a
        // refresh.
        //
        // The body is deliberately EMPTY: with HttpOnly we cannot read the
        // refresh token to send it, and the route now reads it from the
        // cookie. A legacy session that still has one in storage sends it, so
        // both populations refresh through the same call.
        const legacy = getStoredTokens();
        try {
          const refreshRes = await api.post<RefreshResponse>(
            '/auth/refresh',
            legacy ? { refreshToken: legacy.refreshToken } : {},
          );
          const newTokens = refreshRes.data;
          // The server has already re-issued the cookies on this response.
          // Nothing is written to localStorage: storing them again would
          // reinstate the XSS exposure this migration removes.
          const meRes = await api.get<MeResponse>('/auth/me');
          setUser(meRes.data);
          if (meRes.data.impersonated) setIsImpersonating(true);
          setTokens({ accessToken: newTokens.accessToken, refreshToken: newTokens.refreshToken });
        } catch {
          clearAuth();
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  /* ---- Login ---- */
  const login = useCallback(async (email: string, password: string): Promise<User> => {
    setIsLoading(true);
    setError(null);

    try {
      const res = await api.post<LoginResponse>('/auth/login', { email, password });
      const { user: loggedInUser, accessToken, refreshToken } = res.data;

      persistAuth(loggedInUser, accessToken, refreshToken);
      setUser(loggedInUser);
      setTokens({ accessToken, refreshToken });
      setIsLoading(false);
      return loggedInUser;
    } catch (err: unknown) {
      let message = 'Login failed';
      if (err && typeof err === 'object') {
        const e = err as Record<string, unknown>;
        if (e.error && typeof e.error === 'object') {
          const errObj = e.error as Record<string, unknown>;
          if (typeof errObj.message === 'string') message = errObj.message;
        } else if (typeof e.message === 'string') {
          message = e.message;
        }
      }
      setError(message);
      setIsLoading(false);
      throw new Error(message);
    }
  }, []);

  /* ---- Logout ---- */
  const logout = useCallback(() => {
    // AUTH-3. Send the refresh token, or the server revokes NOTHING.
    //
    // This posted `{}`. `logout/route.ts` only revokes when the body carries a
    // `refreshToken` string, so the entire revocation block was skipped on
    // every real sign-out — the server-side work to revoke both token halves
    // existed and was unreachable from the product. Worse, the e2e test posted
    // `{}` as well and asserted success, so the suite reproduced the client's
    // own mistake and stayed green.
    //
    // Read from storage rather than component state: `logout` is also reached
    // on paths where the provider has already been torn down, and storage is
    // the same place `loadStoredAuth` reads.
    const refreshToken =
      typeof window !== 'undefined'
        ? localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
        : null;

    api.post('/auth/logout', refreshToken ? { refreshToken } : {}).catch((err) => {
      if (typeof window !== 'undefined') {
        console.warn('[AuthProvider] Logout request failed:', err?.message);
      }
    });
    clearAuth();
    setUser(null);
    setTokens(null);
    setError(null);
  }, []);

  /* ---- Register ---- */
  const register = useCallback(async (data: { firstName: string; lastName: string; email: string; password: string }) => {
    setIsLoading(true);
    setError(null);

    try {
      await api.post('/auth/register', data);
      setIsLoading(false);
    } catch (err: unknown) {
      const message = (err && typeof err === 'object' && 'error' in err)
        ? ((err as { error: { message: string } }).error?.message ?? 'Registration failed')
        : 'Registration failed';
      setError(message);
      setIsLoading(false);
      throw new Error(message);
    }
  }, []);

  /* ---- Impersonate as chain/hotel ---- */
  const impersonateAs = useCallback(async (params: ImpersonateParams) => {
    // Remember who the admin was, for the banner and for "Return to Admin".
    //
    // Only the USER object: the admin's credential is parked server-side in an
    // HttpOnly cookie by /admin/impersonate. This used to require stored tokens
    // too, which no cookie session has — so no backup was ever written and the
    // banner never knew an impersonation was running.
    const currentUser = getStoredUser();
    if (currentUser) {
      localStorage.setItem(
        STORAGE_KEYS.IMPERSONATION_BACKUP,
        JSON.stringify({ user: currentUser }),
      );
    }

    const res = await api.post<LoginResponse>('/admin/impersonate', params);
    const { user: impUser, accessToken, refreshToken } = res.data;

    persistAuth(impUser, accessToken, refreshToken);
    setUser(impUser);
    setTokens({ accessToken, refreshToken });
    setIsImpersonating(true);
  }, []);

  /* ---- Return to admin from impersonation ---- */
  const returnToAdmin = useCallback(async (): Promise<boolean> => {
    // The server swaps the parked admin credential back into the session
    // cookies; the client then re-reads who it is. Restoring from localStorage
    // alone changed the UI while the cookie still said "hotel".
    try {
      await api.post('/admin/impersonate/exit', {});
      const me = await api.get<MeResponse>('/auth/me');
      persistAuth(me.data, '', '');
      setUser(me.data);
      setTokens(null);
      setIsImpersonating(false);
      localStorage.removeItem(STORAGE_KEYS.IMPERSONATION_BACKUP);
      return true;
    } catch {
      // The parked session expired or was revoked: sign out cleanly rather
      // than leave a hotel session wearing an admin's name.
      clearAuth();
      setUser(null);
      setTokens(null);
      setIsImpersonating(false);
      return false;
    }
  }, []);

  /* ---- Clear error ---- */
  const clearError = useCallback(() => setError(null), []);

  return (
    <AuthContext.Provider
      value={{
        user,
        tokens,
        isAuthenticated: !!user,
        isLoading,
        isImpersonating,
        error,
        login,
        logout,
        register,
        clearError,
        impersonateAs,
        returnToAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used within AuthProvider');
  return ctx;
}
